<template>
    <card>

        <h3 class="mb-0" slot="header">{{ $t('sessions.completion') }}</h3>

        <div v-if="sessionCompleted">

            <div v-if="session.sync">
                
                <base-alert type="warning" class="mb-4">
                    <i class="fas fa-thumbs-up mr-3"></i> {{ $t('sessions.go-online-notice') }}
                </base-alert>

                <base-button outline type="primary" @click.prevent="goOnline" class="ml-1">
                    {{ $t('sessions.go-online') }}
                </base-button>
            </div>

            <div v-else>
                
                <base-alert type="success" class="mb-4">
                    <i class="fas fa-thumbs-up mr-3"></i> {{ $t('sessions.marked-completed-notice') }}
                </base-alert>

                <!--
                <base-button outline type="primary" @click.prevent="goOffline" class="ml-1">
                    {{ $t('sessions.go-offline') }}
                </base-button>
                -->

                <base-button type="primary" :loading="form.recording" @click.prevent="incompleteSession">
                    <i class="fas fa-times mr-1"></i> {{ $t('sessions.mark-incomplete') }}
                </base-button>
            </div>

        </div>

        <div v-else>

            <div v-if="session.sync">
                
                <base-alert type="warning" class="mb-4">
                    <i class="fas fa-thumbs-up mr-3"></i> {{ $t('sessions.go-online-notice') }}
                </base-alert>

                <base-button outline type="primary" @click.prevent="goOnline" class="ml-1">
                    {{ $t('sessions.go-online') }}
                </base-button>
            </div>

            <div v-else>
                
                <base-alert type="warning" class="mb-4">
                    <i class="fas fa-exclamation-triangle mr-3"></i> {{ $t('sessions.marked-incomplete-notice') }}
                </base-alert>

                <!--
                <base-button outline type="primary" @click.prevent="goOffline" class="ml-1">
                    {{ $t('sessions.go-offline') }}
                </base-button>
                -->

                <base-button type="primary" :loading="form.recording" @click.prevent="completeSession">
                    <i class="fas fa-check mr-1"></i> {{ $t('sessions.mark-completed') }}
                </base-button>
            </div>

        </div>

        <portal to="modals">

            <sync-modal v-model="modals.offlineSync" :sync="sync" :autoRemoveAfterSync="true" @done="syncDone">
            </sync-modal>

            <base-modal
                v-model="modals.offlineError"
                size="sm"
                modal-classes="modal-danger"
                modal-content-classes="bg-gradient-danger"
            >
                <div class="py-3 text-center">
                    <i class="tim-icons icon-t-warning icon-3x"></i>
                    <h4 class="heading mt-4">{{ offlineErrorMessage }}</h4>
                </div>
            </base-modal>

            <confirm-modal v-model="modals.confirmCompletion">
                <p>{{ $t('sessions.complete-confirm') }}</p>
            </confirm-modal>

            <error-modal v-model="modals.rejectCompletion">
                <p>{{ $t('sessions.complete-reject') }}</p>
            </error-modal>

        </portal>
    </card>
</template>

<script>
    import CrudClient from '@trax/classes/CrudClient'
    import CrudForm from '@trax/classes/CrudForm'
    import ModalsController from '@trax/classes/ModalsController'
    import SessionFeatures from '../SessionFeatures'
    import SyncModal from '../../offline/SyncModal'

    export default {

        components: {
            SyncModal
        },

        props: {
            session: {
                type: Object,
                required: true
            },

            progress: {
                type: Object,
                required: true
            },

        },

        data() {
            let features = new SessionFeatures(this.$router, this.$auth, this.session)
            let client = new CrudClient('/socrates/api/front/dashboard/store/sessions')
            return {
                features,
                form: new CrudForm(client, features.mapData, features.unmapData, {
                    updated: this.updated
                }),

                // Offline.
                modals: new ModalsController(['offlineSync', 'offlineError', 'confirmCompletion', 'rejectCompletion']),
                offlineErrorMessage: null,
                sync: {
                    endpoint: '/socrates/api/front/offline/sessions',
                    params: {
                        session_id: this.session.id,
                        registration_id: this.session.registration_id,
                        simulation_id: this.session.simulation_id,
                        force: false,
                    },
                },
            }
        },

        created() {
            this.modals.init(this, 'modals')
            this.form.init(this, 'form')
            this.sessionChanged()
        },

        watch: {
            session() {
                this.sessionChanged()
            }
        },

        computed: {

            sessionCompleted() {
                return this.form.data.status == 'completed'
            },

            evaluationStatus() {
                const accessorIds = Object.keys(this.progress)
                const progress1 = this.evaluationProgress(accessorIds[0])

                if (accessorIds.length == 1) {
                    if (progress1 == 0) {
                        return 'not-attempted'
                    }
                    if (progress1 == 100) {
                        return 'completed'
                    }
                    return 'incomplete'
                }

                const progress2 = this.evaluationProgress(accessorIds[1])

                if (progress1 == 0 || progress2 == 0) {
                    return 'not-attempted'
                }
                if (progress1 == 100 || progress2 == 100) {
                    return 'completed'
                }
                return 'incomplete'
            },
        },

        methods: {
            
            updated(resp) {
                this.$emit('reload-session')
            },

            sessionChanged() {
                this.form.update(this.session)
            },

            evaluationProgress(testerId) {
                return this.progress[testerId] && this.progress[testerId].evaluation
                    ? this.progress[testerId].evaluation
                    : 0;
            },

            completeSession() {
                if (this.evaluationStatus == 'not-attempted') {
                    this.modals.open('rejectCompletion')
                    return
                }
                if (this.evaluationStatus == 'incomplete') {
                    this.modals.open('confirmCompletion').then(resp => {
                        if (resp.confirmed) {
                            this.completeSessionNow()
                        }
                    })
                } else {
                    this.completeSessionNow()
                }
            },

            completeSessionNow() {
                this.loading = true
                this.form.data.status = 'completed'
                this.form.data = { ...this.form.data}
            },

            incompleteSession() {
                this.loading = true
                this.form.data.status = 'started'
                this.form.data = { ...this.form.data}
            },

            // Offline.

            goOffline() {
                this.downloadSession(true)
            },

            downloadSession() {
                this.$loader.show()
                axios.post(this.sync.endpoint, {
                    session_id: this.session.id,
                    registration_id: this.session.registration_id,
                    simulation_id: this.session.simulation_id,
                    force: true
                })
                .then(resp => {
                    this.$loader.hide()
                    this.$emit('reload-session')
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            goOnline() {
                if (this.session.sync.color == 'danger')  {
                    this.syncSession()
                } else {
                    this.removeOfflineSession()
                }
            },

            syncSession() {
                this.modals.open('offlineSync')
            },

            syncDone() {
                this.removeOfflineSession()
            },

            removeOfflineSession() {
                this.$loader.show()
                axios.delete(this.sync.endpoint, { data: {
                    session_id: this.session.id,
                    registration_id: this.session.registration_id,
                    simulation_id: this.session.simulation_id,
                }})
                .then(resp => {
                    this.$loader.hide()
                    this.$emit('reload-session')
                }).catch(error => {
                    this.$loader.hide()
                    this.handleOfflineError(error)
                })
            },

            handleOfflineError(error) {
                if (!error.response) {
                    return
                }
                switch(error.response.status) {
                    case 409:
                        this.modals.open('offlineConflict')
                        break
                    case 403:
                    case 404:
                    case 405:
                    case 444:
                        this.offlineErrorMessage = this.$t('pwa.http-error-' + error.response.status)
                        this.modals.open('offlineError')
                        break
                    default:
                        this.$auth.handleErrorVm(error, this)
                }
            },
        }
    }
</script>

